import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const useThemeStore = create(
  persist(
    (set, get) => ({
      theme: 'light', // 'light', 'dark', or 'system'

      // Theme options enabled by admin - default only light is enabled
      enabledThemes: {
        light: true,
        dark: true,
        system: true
      },

      setTheme: (theme) => {
        const { enabledThemes } = get();
        // Only allow setting enabled themes
        if (enabledThemes[theme]) {
          set({ theme });
          applyTheme(theme);
        }
      },

      setEnabledThemes: (themes) => {
        const currentTheme = get().theme;
        set({ enabledThemes: themes });

        // If current theme is now disabled, switch to first enabled one
        if (!themes[currentTheme]) {
          const firstEnabled = Object.entries(themes).find(([_, enabled]) => enabled)?.[0] || 'light';
          set({ theme: firstEnabled });
          applyTheme(firstEnabled);
        }
      },

      toggleTheme: () => {
        const { theme: current, enabledThemes } = get();
        const enabledList = Object.entries(enabledThemes)
          .filter(([_, enabled]) => enabled)
          .map(([key]) => key);

        if (enabledList.length <= 1) return; // No toggle if only one option

        const currentIndex = enabledList.indexOf(current);
        const nextIndex = (currentIndex + 1) % enabledList.length;
        const newTheme = enabledList[nextIndex];

        set({ theme: newTheme });
        applyTheme(newTheme);
      },

      initTheme: () => {
        let { theme, enabledThemes } = get();

        // SiteNav historically used plain localStorage `theme`; merge once into persisted store
        try {
          const legacy = localStorage.getItem('theme');
          if (legacy === 'dark' || legacy === 'light') {
            set({ theme: legacy });
            theme = legacy;
            localStorage.removeItem('theme');
          }
        } catch {
          /* ignore */
        }

        // If current theme is disabled, switch to first enabled
        if (!enabledThemes[theme]) {
          const firstEnabled = Object.entries(enabledThemes).find(([_, enabled]) => enabled)?.[0] || 'light';
          set({ theme: firstEnabled });
          applyTheme(firstEnabled);
        } else {
          applyTheme(theme);
        }
      },

      getEnabledCount: () => {
        const { enabledThemes } = get();
        return Object.values(enabledThemes).filter(Boolean).length;
      }
    }),
    {
      name: 'chatbothub_theme',
    }
  )
);

// Apply theme to document
function applyTheme(theme) {
  const root = document.documentElement;

  if (theme === 'system') {
    const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (systemDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  } else if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

// Listen for system theme changes
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    const store = useThemeStore.getState();
    if (store.theme === 'system') {
      applyTheme('system');
    }
  });
}
